import React, { useState, useEffect, useCallback } from 'react';
import { classificationService } from '../../services/classificationService';

const FamilyForm = ({
  isOpen,
  onClose,
  family = null,
  onSuccess,
  apiService = classificationService,
  /** Si se pasa, no se hace GET de subcategorías (p. ej. RecetaClassificationContext) */
  preloadedSubcategories,
}) => {
  const [formData, setFormData] = useState({
    subcategory_id: '',
    code: '',
    name: '',
    description: '',
    is_active: true,
  });
  const [subcategories, setSubcategories] = useState([]);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [codeGenerated, setCodeGenerated] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const selectableSubcategories = subcategories.filter((sub) => sub?.receta_subcategory_id == null);

  const loadSubcategories = useCallback(async () => {
    setLoadingSubcategories(true);
    try {
      const response = await apiService.getSubcategories({ active_only: false });
      if (response.success) {
        setSubcategories(response.data);
      }
    } catch (error) {
      console.error('Error al cargar subcategorías:', error);
    } finally {
      setLoadingSubcategories(false);
    }
  }, [apiService]);

  useEffect(() => {
    if (!isOpen) return;
    if (Array.isArray(preloadedSubcategories)) {
      setSubcategories(preloadedSubcategories);
      setLoadingSubcategories(false);
      return;
    }
    loadSubcategories();
  }, [isOpen, preloadedSubcategories, loadSubcategories]);

  useEffect(() => {
    if (family) {
      // Modo edición
      setFormData({
        subcategory_id: family.subcategory_id || family.subcategory?.id || '',
        code: family.code || '',
        name: family.name || '',
        description: family.description || '',
        is_active: family.is_active !== undefined ? family.is_active : true,
      });
      setCodeGenerated(true);
    } else {
      // Modo creación - generar código alfanumérico aleatorio
      const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
      let randomCode = '';
      for (let i = 0; i < 4; i++) {
        randomCode += characters.charAt(Math.floor(Math.random() * characters.length));
      }
      setFormData({
        subcategory_id: '',
        code: randomCode,
        name: '',
        description: '',
        is_active: true,
      });
      setCodeGenerated(true);
    }
    setErrors({});
  }, [family, isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Para el código: alfanumérico (números y letras), máximo 4 caracteres
    if (name === 'code') {
      // Solo permitir números y letras, convertir a mayúsculas
      processedValue = value.replace(/[^0-9A-Za-z]/g, '').toUpperCase();
      // Limitar a 4 caracteres
      if (processedValue.length > 4) {
        processedValue = processedValue.slice(0, 4);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };
  
  const generateRandomCode = () => {
    const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    let randomCode = '';
    for (let i = 0; i < 4; i++) {
      randomCode += characters.charAt(Math.floor(Math.random() * characters.length));
    }
    setFormData(prev => ({
      ...prev,
      code: randomCode,
    }));
    setCodeGenerated(true);
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.subcategory_id) {
      newErrors.subcategory_id = 'La subcategoría es obligatoria';
    }

    if (!formData.code.trim()) {
      newErrors.code = 'El código es obligatorio';
    } else if (formData.code.length !== 4) {
      newErrors.code = 'El código debe tener exactamente 4 caracteres';
    } else if (!/^[0-9A-Z]{4}$/.test(formData.code)) {
      newErrors.code = 'El código debe contener solo números y letras';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if (formData.name.length > 100) {
      newErrors.name = 'El nombre no puede tener más de 100 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    console.log('📝 [FamilyForm] Enviando formulario...');
    console.log('📦 [FamilyForm] Datos:', formData);

    try {
      let response;
      if (family) {
        // Actualizar - no enviar código ni subcategory_id (no se pueden modificar)
        const { code, subcategory_id, ...updateData } = formData;
        console.log('🔄 [FamilyForm] Actualizando familia:', family.id);
        response = await apiService.updateFamily(family.id, updateData);
        console.log('✅ [FamilyForm] Familia actualizada exitosamente');
      } else {
        // Crear - no enviar código (se genera en el backend)
        const { code, ...createData } = formData;
        console.log('➕ [FamilyForm] Creando nueva familia');
        response = await apiService.createFamily(createData);
        console.log('✅ [FamilyForm] Familia creada exitosamente');
      }

      console.log('📊 [FamilyForm] Respuesta:', response);

      if (response.success) {
        onSuccess();
        onClose();
      } else {
        // Manejar errores del servidor
        if (response.errors) {
          setErrors(response.errors);
        } else {
          setErrors({ submit: response.message || 'Error al guardar la familia' });
        }
      }
    } catch (error) {
      console.error('❌ [FamilyForm] Error:', error);
      console.error('📊 [FamilyForm] Respuesta del servidor:', error.response?.data);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ submit: error.response?.data?.message || 'Error al guardar la familia' });
      }
    } finally {
      setLoading(false);
      console.log('🏁 [FamilyForm] Proceso finalizado');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {family ? 'Editar Familia' : 'Nueva Familia'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-4">
          {errors.submit && (
            <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md text-sm border border-red-200">
              {errors.submit}
            </div>
          )}

          {/* Subcategoría */}
          <div className="mb-4">
            <label htmlFor="subcategory_id" className="block text-sm font-medium text-gray-700 mb-1">
              Subcategoría <span className="text-red-500">*</span>
            </label>
            <select
              id="subcategory_id"
              name="subcategory_id"
              value={formData.subcategory_id}
              onChange={handleChange}
              disabled={loadingSubcategories || !!family}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.subcategory_id
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              } ${loadingSubcategories || family ? 'bg-gray-100 cursor-not-allowed' : ''}`}
            >
              <option value="">Seleccione una subcategoría</option>
              {selectableSubcategories.map(sub => (
                <option key={sub.id} value={sub.id}>
                  {sub.code} - {sub.name} ({sub.category?.code} - {sub.category?.name})
                </option>
              ))}
            </select>
            {errors.subcategory_id && (
              <p className="mt-1 text-sm text-red-600">{errors.subcategory_id}</p>
            )}
            {loadingSubcategories && (
              <p className="mt-1 text-xs text-gray-500">Cargando subcategorías...</p>
            )}
            {family && (
              <p className="mt-1 text-xs text-gray-500">La subcategoría no se puede modificar</p>
            )}
          </div>

          {/* Código */}
          <div className="mb-4">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              Código <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                id="code"
                name="code"
                value={formData.code}
                onChange={handleChange}
                maxLength={4}
                readOnly={!!family} // Solo lectura en modo edición
                className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                  errors.code
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-teal-500'
                } ${family ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                placeholder="A1B2"
              />
              {!family && (
                <button
                  type="button"
                  onClick={generateRandomCode}
                  className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors text-sm"
                  title="Generar nuevo código"
                >
                  🔄
                </button>
              )}
            </div>
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {family ? 'El código no se puede modificar' : '4 caracteres alfanuméricos (se genera automáticamente)'}
            </p>
          </div>

          {/* Nombre */}
          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={100}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-gray-300 focus:ring-teal-500'
              }`}
              placeholder="Ej: Smartphones"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">Máximo 100 caracteres</p>
          </div>

          {/* Descripción */}
          <div className="mb-4">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Descripción
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Descripción de la familia (opcional)"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description}</p>
            )}
          </div>

          {/* Estado Activo */}
          <div className="mb-6">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              />
              <span className="ml-2 text-sm text-gray-700">Familia activa</span>
            </label>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? 'Guardando...' : family ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FamilyForm;

